/** Navigation helpers: Merchant Data Hub → Credit Card Reports / Chargebacks */

export const MDH_HUB_PATH = '/credit-card';
export const MDH_FROM_PARAM = 'from';
export const MDH_FROM_VALUE = 'merchant-data-hub';
export const MDH_PROPERTY_CODE_PARAM = 'propertyCode';
export const MDH_PROPERTY_NAME_PARAM = 'propertyName';
export const MDH_REPORT_PARAM = 'report';
export const MDH_SUB_REPORT_PARAM = 'subReport';

/**
 * Build View All URL. Always marks navigation as from the hub.
 * When propertyCode is set, detail screens lock that property and hide the selector.
 * When omitted, detail screens limit rows to the user's assigned properties.
 * Optional report / subReport open Credit Card Reports on that selection.
 */
export function buildMdhViewAllPath(
  basePath,
  { propertyCode, propertyName, report, subReport } = {}
) {
  const params = new URLSearchParams();
  params.set(MDH_FROM_PARAM, MDH_FROM_VALUE);
  if (propertyCode) {
    params.set(MDH_PROPERTY_CODE_PARAM, String(propertyCode));
    if (propertyName) {
      params.set(MDH_PROPERTY_NAME_PARAM, String(propertyName));
    }
  }
  if (report) {
    params.set(MDH_REPORT_PARAM, String(report));
  }
  if (subReport) {
    params.set(MDH_SUB_REPORT_PARAM, String(subReport));
  }
  const qs = params.toString();
  return qs ? `${basePath}?${qs}` : basePath;
}

export function readMdhNavState(searchParams) {
  if (!searchParams) {
    return {
      fromHub: false,
      propertyLocked: false,
      propertyCode: null,
      propertyName: null,
      report: null,
      subReport: null,
    };
  }
  const fromHub = searchParams.get(MDH_FROM_PARAM) === MDH_FROM_VALUE;
  const propertyCode = (searchParams.get(MDH_PROPERTY_CODE_PARAM) || '').trim() || null;
  const propertyName = (searchParams.get(MDH_PROPERTY_NAME_PARAM) || '').trim() || null;
  const report = (searchParams.get(MDH_REPORT_PARAM) || '').trim() || null;
  const subReport = (searchParams.get(MDH_SUB_REPORT_PARAM) || '').trim() || null;
  return {
    fromHub,
    propertyLocked: Boolean(fromHub && propertyCode),
    propertyCode,
    propertyName,
    report,
    subReport,
  };
}

/** Snowflake PROPERTY_ID = Postgres property_code (dropdown label prefix before " - "). */
export function propertyCodeForApi(prop) {
  if (!prop) return null;
  const explicit = prop.propertyCode ?? prop.PROPERTY_CODE;
  if (explicit != null && String(explicit).trim()) {
    return String(explicit).trim();
  }
  const label = prop.propertyName ?? prop.PROPERTY_NAME;
  if (label == null || !String(label).trim()) return null;
  const full = String(label).trim();
  const sep = ' - ';
  const i = full.indexOf(sep);
  if (i > 0) return full.slice(0, i).trim();
  return full;
}

/** Unique property codes from the signed-in user's Users-screen assignments. */
export function assignedPropertyCodes(properties) {
  const codes = [];
  const seen = new Set();
  (properties || []).forEach((prop) => {
    const code = propertyCodeForApi(prop);
    if (code && !seen.has(code)) {
      seen.add(code);
      codes.push(code);
    }
  });
  return codes;
}

/**
 * One selected property stays on the query string as property_id.
 * No selection POSTs the user's assigned codes. A long property_ids query
 * string is rejected by the auth gateway before CORS headers are added.
 * An empty assignment posts [] so the API matches nothing.
 */
export function applyPropertyScope(params, selectedCode, assignedCodes) {
  const selected = selectedCode != null ? String(selectedCode).trim() : '';
  if (selected) {
    params.append('property_id', selected);
    return { method: 'GET', body: undefined };
  }
  const codes = [];
  const seen = new Set();
  (assignedCodes || []).forEach((code) => {
    const trimmed = String(code).trim();
    if (trimmed && !seen.has(trimmed)) {
      seen.add(trimmed);
      codes.push(trimmed);
    }
  });
  return {
    method: 'POST',
    body: JSON.stringify({ property_ids: codes }),
  };
}

/**
 * Display label for breadcrumb: avoid "CODE - CODE - Name" when propertyName
 * already includes the code (common when API stores combined labels).
 */
export function formatMdhPropertyLabel(propertyCode, propertyName) {
  const code = (propertyCode || '').trim();
  const name = (propertyName || '').trim();
  if (!code && !name) return null;
  if (!name) return code;
  if (!code) return name;
  if (name === code || name.startsWith(`${code} - `) || name.startsWith(`${code}-`)) {
    return name;
  }
  return `${code} - ${name}`;
}
